import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import styled from "styled-components"
import { Input } from "components/ui/inputs/Input";
import { Table } from "components/ui/display/Table";
import { calculationLimits, designFastVariables } from "store/calculation.config";
import { useEffect, useState } from "react";
import { useErrors } from "providers/ErrorsProvider";
import { calculateVertical } from "./Vertical.calculate";
import { MaterialIcon } from "components/ui/shared/MaterialIcon"


export const Vertical = () => {
    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        firstSlope: '',
        secondSlope: '',
        horizontalCurveRadius: '',
        lightsHeight: '',
        lightsAngle: '',
    });

    const [calculationInput, setCalculationInput] = useState({
        designClass: '',
        speedlimit: '',
        firstSlope: '',
        secondSlope: '',
        horizontalCurveRadius: '',
        lightsHeight: '',
        lightsAngle: '',
    });

    useEffect(() => {
        const defaultValues = {
            lightsHeight: String(designFastVariables.lightHeight),
            lightsAngle: String(designFastVariables.lightAngle),
        }
        
        setRawInput(prev => ({
            ...prev,
            ...defaultValues,
        }));

        setCalculationInput(prev => ({
            ...prev,
            ...defaultValues,
        }));
    }, []);

    

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateVertical(calculationInput);
        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }
        
        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });
        
    }, [calculationInput, setError, unsetError]);
    
    return (
        <StyledVertical>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title="Inndata">
                    <DropdownMenu
                        label="Dimensjoneringsklasse"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='road' weight={300}/>}
                        defaultName='Velg dimensjoneringsklasse'
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, designClass: e}))}}
                    >
                        <DropdownOption value='h1-80' name='H1 (80km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h1-90' name='H1 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h2-90' name='H2 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-90' name='H3 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-100' name='H3 (100km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-110' name='H3 (110km/t) - Nasjonal hovedveg'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error'}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning'}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Første stigning"
                        prefix={<span>s<sub>1</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.firstSlope?.severity === 'error'}
                        warning={calculatedState.input?.firstSlope?.severity === 'warning'}
                        value={rawInput.firstSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, firstSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, firstSlope: e.target.value}))}
                    />
                    <Input
                        title="Andre stigning"
                        prefix={<span>s<sub>2</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.secondSlope?.severity === 'error'}
                        warning={calculatedState.input?.secondSlope?.severity === 'warning'}
                        value={rawInput.secondSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, secondSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, secondSlope: e.target.value}))}
                    />
                    <Input
                        title="Horisontalkurveradius"
                        prefix={<span>R<sub>h</sub></span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.horizontalCurveRadius?.severity === 'error'}
                        warning={calculatedState.input?.horizontalCurveRadius?.severity === 'warning'}
                        value={rawInput.horizontalCurveRadius}
                        onChange={(e) => setRawInput(prev => ({...prev, horizontalCurveRadius: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, horizontalCurveRadius: e.target.value}))}
                    />
                    <Input
                        title="Lysets høyde over bakken"
                        prefix={<span>a<sub>4</sub></span>}
                        sufix='m'
                        type='number'
                        min={calculationLimits.minLightsHeight}
                        max={calculationLimits.maxLightsHeight}
                        step={0.1}
                        error={calculatedState.input?.lightsHeight?.severity === 'error'}
                        warning={calculatedState.input?.lightsHeight?.severity === 'warning'}
                        value={rawInput.lightsHeight}
                        onChange={(e) => setRawInput(prev => ({...prev, lightsHeight: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, lightsHeight: e.target.value}))}
                    />
                    <Input
                        title="Lysets åpningsvinkel"
                        prefix={<span>ψ</span>}
                        sufix='°'
                        type='number'
                        min={calculationLimits.minLightsAngle}
                        max={calculationLimits.maxLightsAngle}
                        step={0.01}
                        error={calculatedState.input?.lightsAngle?.severity === 'error'}
                        warning={calculatedState.input?.lightsAngle?.severity === 'warning'}
                        value={rawInput.lightsAngle}
                        onChange={(e) => setRawInput(prev => ({...prev, lightsAngle: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, lightsAngle: e.target.value}))}
                    />
                </ToolsContainer>
                <ToolsContainer title="Faste variabler">
                    <Input
                        title="Beregningsmessig øyehøyde"
                        prefix={<span>a<sub>1</sub></span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.computationalEyeHeight?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Beregningsmessig objekthøyde"
                        prefix={<span>a<sub>2</sub></span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.computationalObjectHeight?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Beregningsmessig kjøretøyhøyde"
                        prefix={<span>a<sub>3</sub></span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.computationalVehicleHeight?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Bremsefriksjon"
                        prefix={<span>f<sub>b</sub></span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.brakingFriciton?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Sikkerhetsfaktor"
                        prefix={<span>f</span>}
                        sufix=''
                        type='number'
                        value={calculatedState.derived?.safetyFactor?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fartstillegg"
                        prefix={<span>∆ v<sub>t</sub></span>}
                        sufix='km/t'
                        value={calculatedState.derived?.speedAddition?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fartsprofiltillegg"
                        prefix={<span>∆ v<sub>pt</sub></span>}
                        sufix='km/t'
                        value={calculatedState.derived?.speedProfileAddition?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Reaksjonstid"
                        prefix={<span>t<sub>r</sub></span>}
                        sufix='s'
                        value={calculatedState.derived?.reactionTime?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Vertikalakselerasjon"
                        prefix={<span>a<sub>v</sub></span>}
                        sufix={<span>m/s<sup>2</sup></span>}
                        value={calculatedState.derived?.verticalAcceleration?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Stopplengde"
                    prefix={<span>L<sub>k</sub></span>}
                    value={calculatedState.output?.stopDistance?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Vertikalkurve i høybrekk (lang)"
                    prefix={<span>R<sub>v,høy,min</sub></span>}
                    value={calculatedState.output?.verticalLongCrestCurveRadius?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på vertikalkurve"
                    prefix={<span>L<sub>v,høy,min</sub></span>}
                    value={calculatedState.output?.verticalLongCrestCurveLength?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Vertikalkurve i høybrekk (kort)"
                    prefix={<span>R<sub>v,høy,min</sub></span>}
                    warning={calculatedState.output?.verticalShortCrestCurveRadius?.severity === 'warning'}
                    value={calculatedState.output?.verticalShortCrestCurveRadius?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Vertikalkurve i lavbrekk"
                    prefix={<span>R<sub>v,lav,min</sub></span>}
                    value={calculatedState.output?.verticalNormalSagCurveRadius?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Vertikalkurve i lavbrekk (belsysningsbetinget)"
                    prefix={<span>R<sub>v,lav,min</sub></span>}
                    value={calculatedState.output?.verticalDarkSagCurveRadius?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.input?.designClass?.valid ?
                    <ToolsContainer title="Dimensjoneringstabell">
                        <Table
                            headers={[
                                {key: 'minRh', label: <span>R<sub>h,min</sub></span>},
                                {key: 'minA', label: <span>A<sub>min</sub></span>},
                                {key: 'minRvCrest', label: <span>R<sub>v,høy,min</sub></span>},
                                {key: 'minRvSag', label: <span>R<sub>v,lav,min</sub></span>},
                                {key: 'e', label: <span>e</span>, unit: '[m/m]'},
                                {key: 's', label: <span>s</span>, unit: '[m/m]'},
                            ]}
                            data={calculatedState.input.designClass.value.designTable}
                        />
                    </ToolsContainer>

                    : null
                }
            </ToolsColumn>
        </StyledVertical>
    )
}

const StyledVertical = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: auto;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }
`
